import { ApolloClient, ApolloLink, InMemoryCache, HttpLink, gql } from '@apollo/client'
import { GraphQLError } from 'graphql'

import { getStoredRefreshToken, hasStoredLogin } from './storedState'
import { createConvertScalarsLink } from './convertScalarLink'
import { createErrorLink } from './links/errorLink'
import { createAuthCheckLink } from './links/authCheckLink'
import { createRequestLink } from './links/requestLink'
import { LocalState } from '@apollo/client/local-state'
import { RetryLink } from '@apollo/client/link/retry'

interface LinkState {
  authenticated: boolean
}

export const LOGIN_CLIENTSTATE_MUTATION = gql`
  mutation loginClientState {
    login @client
  }
`

export const IS_AUTHENTICATED_QUERY = gql`
  query isAuthenticated {
    authenticated @client
  }
`

const defaults: LinkState = {
  authenticated: !!getStoredRefreshToken() || hasStoredLogin(),
}

export const createGraphqlClient = () => {
  let introspectionQueryResultData

  try {
    introspectionQueryResultData = require('../generated/apollo/fragmentTypes.json')
  } catch (e) {
    throw new Error('Fragment types are not generated. You should run: yarn codegen to generate fragment types')
  }

  const possibleTypes: Record<string, string[]> = {}
  if (introspectionQueryResultData && introspectionQueryResultData.__schema && introspectionQueryResultData.__schema.types) {
    introspectionQueryResultData.__schema.types.forEach((supertype: any) => {
      if (supertype.possibleTypes) {
        possibleTypes[supertype.name] = supertype.possibleTypes.map((subtype: any) => subtype.name)
      }
    })
  }

  const cache = new InMemoryCache({ possibleTypes })
  const convertScalarsLink = createConvertScalarsLink()

  const errorLink = createErrorLink()
  const authCheckLink = createAuthCheckLink()
  const requestLink = createRequestLink()
  const retryLink = new RetryLink({
    attempts: {
      max: 3,
      retryIf: (error, operation) =>{ 
        console.debug('Retrying operation: ', { error, operationName: operation.operationName, operationType: operation.operationType })  
        return !!error && operation.operationType !== 'mutation'
       },
    },
    delay: {
      initial: 300,
      max: 5000,
      jitter: true,
    },
  })

  const apolloClient = new ApolloClient({
    defaultOptions: {
      watchQuery: {
        notifyOnNetworkStatusChange: false,
      },
    },
    link: ApolloLink.from([
      errorLink,
      authCheckLink,
      requestLink,
      convertScalarsLink,
      retryLink,
      new HttpLink({
        uri: '/.netlify/functions/graphql',
        credentials: 'same-origin'
      }),
    ]), 
    cache,
    localState: new LocalState({
      resolvers: {
       Mutation: {
          login: (_1: any, _2: any, context: any) => {
            context.client.cache.writeQuery({
              query: IS_AUTHENTICATED_QUERY,
              data: { authenticated: true },
            })
            return null
          },
        },
      },
    }),
  })

  cache.writeQuery({
    query: IS_AUTHENTICATED_QUERY,
    data: defaults,
  })

  return apolloClient
}

export function createUnauthorizedError(msg = 'UnauthorizedError') {
  return new GraphQLError('Unauthorized error', {
    extensions: {
      code: 'UNAUTHENTICATED',
      originalError: msg,
    },
  })
}
